import React from "react";
import { Grid2 as Grid, Typography } from "@mui/material";
import { useEntityHandlerContext } from "../../../controllers/entityController";
import {
  LanguageItem,
  LanguagesList,
} from "../../forms/common/subForms/LanguagesListField";
import ViewSubCard from "./ViewSubCard";
import ViewMultiSelectionRow from "./ViewMultiSelectionRow";

interface ViewLanguagesFieldProps {
  name: string;
  /**
   * Optional label printed above each LanguageItem subcard,
   * suffixed with the row index (e.g. "Language Group 1").
   */
  rowLabel?: string;
}

/**
 * Renders a `LanguagesList` (array of `{ languages, roles }`) in read-only mode.
 * Each `LanguageItem` is wrapped in a nested `ViewSubCard` containing two
 * `ViewMultiSelectionRow`s so that it is visually clear which roles belong to which language set.
 */
const ViewLanguagesField: React.FC<ViewLanguagesFieldProps> = ({
  name,
  rowLabel = "Language",
}) => {
  const { methods } = useEntityHandlerContext();
  const data = methods.getValues();
  const rawValue = data?.[name as keyof typeof data];
  const items: LanguagesList = Array.isArray(rawValue)
    ? (rawValue as LanguagesList)
    : [];

  if (items.length === 0) {
    return (
      <Grid size={{ xs: 12 }}>
        <Typography variant="body1">N/A</Typography>
      </Grid>
    );
  }

  return (
    <>
      {items.map((item: LanguageItem, index: number) => (
        <ViewSubCard
          key={index}
          variant="nested"
          title={`${rowLabel} ${index + 1}`}
        >
          <ViewMultiSelectionRow label="Languages" values={item?.languages ?? []} />
          <ViewMultiSelectionRow label="Roles" values={item?.roles ?? []} />
        </ViewSubCard>
      ))}
    </>
  );
};

export default ViewLanguagesField;
